import { useMemo, type ReactNode } from 'react';
import type { TenantPublic } from '@/shared/contracts';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTenant } from '../lib/tenant-context';
import { useMe, useSession } from '../lib/queries';
import { hasProgrammeAccess, isAccountMode } from '@/shared/contracts';
import { usesAcademyWordmark } from '@/shared/wordmark';
import { AcademyLockup, Mark } from './Mark';
import type { NavItem } from './nav';
import { Avatar } from './Avatar';
import { Rail } from './Rail';
import { TabBar } from './TabBar';
import { ThemeToggle } from './Toggle';
import { useTheme } from '../lib/theme';
import { tenantStyle } from '../lib/tenant';
import { SupportLink } from './SupportLink';
import { PlayerBar, usePlayerBar } from './PlayerBar';
import { longDate } from '../lib/format';
import { useFocusClearOfBars } from '../lib/focus-clear';

export const APP_VERSION = 'v2.0';

/**
 * Direction AC names: Learn is the structured shelf (modules, briefs, films), Library is the
 * loose reference surface (the field cards, searchable and saveable), Record is what the learner
 * has earned. Routes are unchanged.
 */
export const LEARNER_NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/library', label: 'Learn', icon: 'learn' },
  { to: '/cards', label: 'Library', icon: 'library' },
  { to: '/credential', label: 'Record', icon: 'account' },
];

/**
 * The destination a route belongs to, for the rail, the tab bar and the status-bar crumb (D12,
 * L-07): an item and its check sit under Learn, a card under Library, the certificate, renewal and
 * the account under Record (the handoff's initials open Record).
 */
export function destinationOf(pathname: string): { path: string; crumb: string } | null {
  if (pathname === '/') return { path: '/', crumb: 'Home' };
  if (pathname === '/library' || pathname.startsWith('/learn/')) return { path: '/library', crumb: 'Learn' };
  if (pathname === '/cards' || pathname.startsWith('/cards/')) return { path: '/cards', crumb: 'Library' };
  if (pathname === '/account') return { path: '/credential', crumb: 'Account' };
  if (pathname === '/credential' || pathname.startsWith('/credential/') || pathname === '/recert')
    return { path: '/credential', crumb: 'Record' };
  if (pathname === '/sponsor' || pathname.startsWith('/sponsor/'))
    return { path: '/sponsor', crumb: 'Sponsor' };
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return { path: '/admin', crumb: 'Admin' };
  if (pathname === '/explore') return { path: '', crumb: 'Explore' };
  return null;
}

/** `AT` from a name, else the first two letters of the email. */
export function initialsOf(name: string | null | undefined, email: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0]![0]}${words[words.length - 1]![0]}`.toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (email ?? '').slice(0, 2).toUpperCase();
}

/**
 * The frame every signed-in screen sits in. On a phone: a header with the mark, the name and the
 * learner's initials, the graphite tab bar, and the player bar above it. On a desk: a white
 * left rail with the mark, the destinations, the access card and the learner, then a status bar
 * and the content column. In endorsed mode a footer says who made the academy. White label shows
 * no Academy mark anywhere.
 */
export function Shell() {
  const tenant = useTenant();
  const session = useSession();
  const user = session.data?.user ?? null;
  // Every programme-scoped read waits on programme access; without it `/me` is refused. Someone
  // signed in with no programme here (possible only with the account layer on) gets no
  // destinations, so the frame has no rail, tab bar or initials, and their account page carries
  // their name and photo.
  const programme = !isAccountMode(session.data) || hasProgrammeAccess(session.data);
  const me = useMe(!!user && programme);
  const membership = session.data?.membership ?? null;
  const { pathname } = useLocation();
  // The player bar is on every signed-in screen with a programme (design pass 3, item 6): the
  // current or next module, the place the server holds and the audio queue.
  const bar = usePlayerBar(!!user && programme);
  // Here rather than in the frame, which the brand preview also renders (inert, in its own document).
  useFocusClearOfBars();
  const destination = destinationOf(pathname);

  const items = useMemo(() => {
    // No programme, no destinations: every one of them is that programme's material.
    if (!programme) return [];
    const nav = [...LEARNER_NAV];
    if (user?.is_superadmin || membership?.role === 'operator_admin') {
      nav.push({ to: '/sponsor', label: 'Sponsor', icon: 'sponsor' });
    }
    if (user?.is_superadmin) nav.push({ to: '/admin', label: 'Admin', icon: 'admin' });
    return nav;
  }, [programme, user?.is_superadmin, membership?.role]);

  const person = user
    ? {
        name: me.data?.user.name ?? user.name ?? null,
        email: user.email,
        photoUrl: me.data?.user.id === user.id ? (me.data?.user.photo_url ?? null) : null,
        accessActive: membership?.status === 'active',
      }
    : null;

  return (
    <ShellFrame
      tenant={tenant}
      items={user ? items : []}
      person={person}
      activePath={destination?.path ?? ''}
      crumb={destination?.crumb}
      themeToggle={<ThemeToggle />}
      strip={bar ? <PlayerBar bar={bar} /> : null}
    >
      <Outlet />
    </ShellFrame>
  );
}

export type ShellPerson = {
  name: string | null;
  email: string;
  photoUrl: string | null;
  accessActive: boolean;
};

/** The real shell geometry is shared by the app and the brand review's isolated viewport. */
export function ShellFrame({
  tenant,
  items,
  themeToggle,
  children,
  activePath,
  crumb,
  photoUrl,
  person,
  strip,
}: {
  tenant: TenantPublic;
  items: NavItem[];
  themeToggle: ReactNode;
  children: ReactNode;
  /** The destination to mark current; the rail and the tab bar match it rather than the address. */
  activePath?: string;
  /** The destination named first in the desktop status bar, before the academy name. */
  crumb?: string | undefined;
  /** Kept for the brand preview, which passes a photo without a person. */
  photoUrl?: string | null | undefined;
  person?: ShellPerson | null | undefined;
  /** The persistent player bar, on every signed-in screen with a programme. */
  strip?: ReactNode;
}) {
  const endorsed = tenant.brand_mode === 'endorsed';
  const signedIn = items.length > 0;
  const initials = person ? initialsOf(person.name, person.email) : null;
  const photo = person?.photoUrl ?? photoUrl ?? null;
  const today = longDate(new Date());

  // The Academy itself takes the Crimson Pro lockup; an operator keeps its logo or mark and name.
  const brand = (
    <Link
      to={signedIn ? '/' : '/explore'}
      className="flex min-h-11 min-w-0 items-center gap-3 no-underline"
      aria-label={`${tenant.name} home`}
    >
      {usesAcademyWordmark(tenant) ? (
        <AcademyLockup className={`wordmark-header text-inkMuted ${signedIn ? 'wordmark-rail' : ''}`} />
      ) : (
        <>
          <Mark name={tenant.name} logoUrl={tenant.logo_url} brandMode={tenant.brand_mode} size={34} />
          <span className="line-clamp-2 text-[15px] font-medium leading-tight text-ink sm:line-clamp-none sm:truncate sm:text-[16px]">
            {tenant.name}
          </span>
        </>
      )}
    </Link>
  );

  // The initials sit on an ink disc: graphite in light, the light ink with dark initials in dark (D3).
  const avatar = signedIn && (photo || initials) && (
    <Link
      to="/account"
      aria-label="Account"
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full no-underline"
    >
      {photo ? (
        <Avatar url={photo} size={40} />
      ) : (
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-ink text-[14px] font-medium text-bgElevated">
          {initials}
        </span>
      )}
    </Link>
  );

  return (
    <div className={`flex min-h-dvh flex-col ${signedIn ? 'rail:flex-row' : ''}`}>
      {/* One header: the top bar on a phone, the left rail on a desk. Signed out there are no
          destinations, so it stays a top bar at every width rather than an empty rail (S-38). */}
      <header
        data-sticky-bar=""
        className={`sticky top-0 z-30 border-b border-rule bg-panel ${
          signedIn ? 'rail:static rail:w-[264px] rail:shrink-0 rail:border-b-0 rail:border-r' : ''
        }`}
      >
        <div
          className={
            signedIn
              ? 'rail:sticky rail:top-0 rail:flex rail:h-dvh rail:flex-col rail:px-4 rail:pb-4 rail:pt-5'
              : ''
          }
        >
          <div
            className={`mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-3 px-4 sm:px-6 ${
              signedIn ? 'rail:h-auto rail:px-2' : 'rail:px-8'
            }`}
          >
            {brand}
            {/* The phone header carries the mark, the name and the initials only; the theme switch
                lives in the desktop status bar and in Account (D2, S-21). */}
            {signedIn ? (
              <div className="flex items-center gap-2 rail:hidden">{avatar}</div>
            ) : (
              <div className="hidden items-center gap-4 rail:flex">{themeToggle}</div>
            )}
          </div>
          {signedIn && <Rail items={items} activePath={activePath} className="mt-6 hidden rail:block" />}
          {signedIn && (
            <div className="mt-auto hidden flex-col gap-3 rail:flex">
              {person && (
                <div className="rounded-[14px] border border-rule bg-abyss p-4 text-[13px] text-inkMuted">
                  <p className="m-0">
                    {person.accessActive ? 'Learning access sponsored by' : 'Learning access through'}{' '}
                    {tenant.name}
                  </p>
                  <Link
                    to="/account"
                    className="mt-3 inline-flex h-11 items-center rounded-[10px] border border-rule2 bg-panel px-4 text-[14px] font-medium text-ink no-underline hover:border-accent hover:text-accentText"
                  >
                    Access details
                  </Link>
                </div>
              )}
              <div className="flex items-center gap-3 px-2 pt-2">
                {avatar}
                {person && (
                  <span className="truncate text-[14px] font-medium text-ink">
                    {person.name ?? person.email}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Desktop status bar. Always mounted while signed in, so its theme switch keeps focus
            across session refetches. The crumb names the destination before the academy (L-13). */}
        {signedIn && (
          <div
            data-sticky-bar=""
            className="sticky top-0 z-30 hidden h-14 items-center justify-between gap-4 border-b border-rule bg-panel px-8 rail:flex"
          >
            <p className="m-0 flex min-w-0 items-center gap-2.5 text-[13.5px] text-inkFaint">
              {crumb && (
                <>
                  <span className="font-medium text-ink">{crumb}</span>
                  <span aria-hidden="true">·</span>
                </>
              )}
              <span className="truncate">{tenant.name}</span>
              {person?.accessActive && (
                <span className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-successTint px-2.5 text-[12.5px] font-medium text-successText">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-success" />
                  Access active
                </span>
              )}
            </p>
            <div className="flex shrink-0 items-center gap-4">
              <span className="text-[12.5px] text-inkFaint">{today}</span>
              {themeToggle}
            </div>
          </div>
        )}

        {/* Signed out there is no status bar and the phone header carries no switch (D2, S-21), so
            below the rail width the switch gets its own row above the page, as on the other public
            pages (S-23). From the rail width the signed-out top bar shows it instead. */}
        {!signedIn && (
          <div className="mx-auto flex w-full max-w-6xl justify-end px-4 pt-4 sm:px-6 rail:hidden">
            {themeToggle}
          </div>
        )}
        <div
          className={`mx-auto flex w-full max-w-6xl flex-1 px-4 pt-6 sm:px-6 has-[[data-module-hero]]:max-w-none rail:px-8 rail:pt-8 ${
            strip ? 'pb-40 rail:pb-28' : signedIn ? 'pb-24 rail:pb-10' : 'pb-10'
          }`}
        >
          <main
            id="main"
            tabIndex={-1}
            data-academy-name={tenant.name}
            className="min-w-0 flex-1 scroll-mt-20 [overflow-wrap:anywhere]"
          >
            {children}
          </main>
        </div>

        <footer
          className={`mx-auto w-full max-w-6xl px-4 pt-2 text-[12px] text-inkFaint sm:px-6 rail:px-8 ${
            strip ? 'pb-40 rail:pb-24' : signedIn ? 'pb-24 rail:pb-8' : 'pb-8'
          }`}
        >
          <div className="hairline flex flex-wrap items-center justify-between gap-x-4 gap-y-1 pt-4">
            {endorsed ? (
              <span className="inline-flex items-center gap-2">
                Powered by <AcademyLockup size={16} />
              </span>
            ) : (
              <span className="min-w-0 [overflow-wrap:anywhere]">{tenant.name}</span>
            )}
            <span className="flex flex-wrap items-center gap-x-4">
              <SupportLink tenant={tenant} topic="Access or content question" className="!text-[12.5px]" />
              <Link
                to="/explore"
                className="inline-flex min-h-11 items-center text-[12.5px] text-accentText no-underline hover:underline"
              >
                About the programme
              </Link>
            </span>
          </div>
        </footer>
      </div>

      {strip}
      {signedIn && <TabBar items={items} activePath={activePath} />}
    </div>
  );
}

/**
 * A centred single-card frame for the public pages: sign-in, check email, not enrolled, verify.
 * The page's own card is the only card (S-02); the theme switch sits in its own row above it, so
 * it can never overlap a tall card (S-23).
 */
export function PublicFrame({ children, brand }: { children: React.ReactNode; brand?: TenantPublic }) {
  const host = useTenant();
  const tenant = brand ?? host;
  const [theme] = useTheme();
  return (
    <div className="flex min-h-dvh flex-col" style={tenantStyle(tenant, theme)}>
      <div className="flex justify-end px-4 pt-4 sm:px-6 sm:pt-5">
        <ThemeToggle />
      </div>
      <main
        id="main"
        tabIndex={-1}
        data-academy-name={tenant.name}
        className="flex flex-1 items-center justify-center px-4 pb-12 pt-6"
      >
        <div className="w-full max-w-[420px] [overflow-wrap:anywhere]">{children}</div>
      </main>
      {(tenant.brand_mode === 'endorsed' || tenant.support_email) && (
        <footer className="pb-6 text-center text-[12px] text-inkFaint">
          <SupportLink tenant={tenant} topic="Academy access" className="mx-4" />
          {tenant.brand_mode === 'endorsed' && (
            <span className="inline-flex items-center gap-2">
              Powered by <AcademyLockup size={14} />
            </span>
          )}
        </footer>
      )}
    </div>
  );
}
